import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Button } from 'primeng/button';
import {
  CheckboxWithLabelComponent,
  FormControlComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
  PrimengPasswordDirective,
  PrimengSelectDirective,
  SelectItem,
} from '@web/shared/ui';
import {
  AbstractControl,
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { Guid, isNil, Nillable, valueOrDefault } from '@web/utils';
import {
  AdminBoxAttributes,
  AdminBoxCharacteristics,
  AdminBoxDetailsForm,
  AdminProductDetailsFormGroup,
} from '../../models/admin-box-details-form';
import { AdminBoxCosmetic } from '../../models/admin-box-cosmetic';
import {
  AdminMultimediaImageUpload,
  MultimediaChooseOrUploadControlComponent,
  MultimediaChooseOrUploadMultipleControlComponent,
} from '@web/shared/module/multimedia';
import { map, Observable, of, startWith, tap } from 'rxjs';
import { Select, SelectFilterEvent } from 'primeng/select';
import { AdminBoxStatus } from '../../enums/admin-box-status';
import { AdminBoxStatusProviderService } from '../../providers/admin-box-status-provider.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Password } from 'primeng/password';
import { InputNumber } from 'primeng/inputnumber';
import { AdminBrandSelectItemsProviderService } from '@web/shared/module/brand';
import { AdminDictionaryBoxCategorySelectItemsProviderService } from '@web/shared/module/dictionary';
import { Checkbox } from 'primeng/checkbox';
import { AdminBoxCharacteristicsControlComponent } from '../admin-box-characteristics-control/admin-box-characteristics-control.component';
import { AdminBoxCharacteristicsValidator } from '../../validators/admin-box-characteristics.validator';
import { DatePicker } from 'primeng/datepicker';
import { AdminBoxCosmeticsControlComponent } from '../admin-box-cosmetics-control/admin-box-cosmetics-control.component';
import {
  AdminWarehouseGoodDataSourceService,
  AdminWarehouseGoodSelectItemsProviderService,
  adminWarehouseGoodSelectItemsWithCurrentSelection,
  WarehouseGoodType,
  WarehouseGoodUsageStatusComponent,
} from '@web/features/warehouse';

@Component({
  selector: 'app-admin-box-details-form',
  imports: [
    AdminBoxCharacteristicsControlComponent,
    AdminBoxCosmeticsControlComponent,
    Button,
    Checkbox,
    CheckboxWithLabelComponent,
    CommonModule,
    FormControlComponent,
    FormsModule,
    InputNumber,
    InputText,
    MultimediaChooseOrUploadControlComponent,
    MultimediaChooseOrUploadMultipleControlComponent,
    Password,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    PrimengPasswordDirective,
    PrimengSelectDirective,
    ReactiveFormsModule,
    Select,
    DatePicker,
    WarehouseGoodUsageStatusComponent,
  ],
  templateUrl: './admin-box-details-form.component.html',
  styleUrl: './admin-box-details-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBoxDetailsFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);
  private readonly brandDataSource = inject(
    AdminBrandSelectItemsProviderService,
  );
  private readonly productStatusProvider = inject(
    AdminBoxStatusProviderService,
  );
  private readonly boxCategoryProvider = inject(
    AdminDictionaryBoxCategorySelectItemsProviderService,
  );
  private readonly warehouseGoodDataSource = inject(
    AdminWarehouseGoodDataSourceService,
  );
  private readonly warehouseGoodSelectItemsProvider = inject(
    AdminWarehouseGoodSelectItemsProviderService,
  );
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  public readonly data = input<Nillable<AdminBoxDetailsForm>>();
  public readonly isEditContext = input<boolean>(false);
  public readonly submitClickEvent = output<AdminBoxDetailsForm>();

  protected readonly boxStatusSelectItems$ = this.productStatusSelectItems();
  protected readonly boxCategorySelectItems$ = this.boxCategorySelectItems();
  protected readonly brandSelectItems$ = this.brandSelectItems();
  protected readonly form = this.buildForm();
  protected readonly valueOrDefault = valueOrDefault;
  protected readonly warehouseGoodType = WarehouseGoodType;
  protected warehouseGoodSelectItems$: Observable<SelectItem<Guid>[]> = of([]);
  protected currentGoodId$!: Observable<Nillable<Guid>>;
  protected shouldPasswordVisible$!: Observable<boolean>;

  public constructor() {
    this.initializeWarehouseGoodAvailabilityEffect();
  }

  public ngOnInit(): void {
    this.listenToStatusValueChanges();
    this.configureWarehouseGoodSelectItemsSource();
    this.configureCurrentGoodIdSource();
    this.configureShouldPasswordVisibleSource();
    this.configureSlugControlRequirements(this.isEditContext());
    this.fillByData();
  }

  public handleWarehouseGoodSelectFilterChange(event: SelectFilterEvent): void {
    this.warehouseGoodSelectItems$ = this.warehouseGoodSelectItems(
      event.filter.toLowerCase(),
    );
    this.changeDetectorRef.detectChanges();
  }

  public handleSubmitClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  private listenToStatusValueChanges(): void {
    this.form.controls.status.valueChanges
      .pipe(
        startWith(this.form.controls.status.value),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((status) => {
        const isPublicOrProtected = this.isPublicOrProtected(status);
        const isDraft = this.isDraft(status);

        this.configureFeaturedFileRequirements(!isDraft);
        this.configureIllustrationFileRequirements(isPublicOrProtected);
        this.configureSymbolFileRequirements(isPublicOrProtected);
        this.configurePriceRequirements(!isDraft);
        this.configureCosmeticBrandIdRequirements(!isDraft);
        this.configureCharacteristicsRequirements(isPublicOrProtected);
      });
  }

  private isPublicOrProtected(status: Nillable<AdminBoxStatus>): boolean {
    return (
      !isNil(status) &&
      [AdminBoxStatus.Public, AdminBoxStatus.Protected].includes(status)
    );
  }

  private isProtected(status: Nillable<AdminBoxStatus>): boolean {
    return !isNil(status) && [AdminBoxStatus.Protected].includes(status);
  }

  private isDraft(status: Nillable<AdminBoxStatus>): boolean {
    return !isNil(status) && [AdminBoxStatus.Draft].includes(status);
  }

  private toData(): AdminBoxDetailsForm {
    const data = this.form.getRawValue();

    return new AdminBoxDetailsForm({
      attributes: data.attributes,
      characteristics: data.characteristics,
      featuredFile: data.featuredFile,
      files: data.files,
      illustrationFile: data.illustrationFile,
      name: data.name,
      password: data.password,
      price: data.price,
      availableQuantity: data.availableQuantity,
      slug: data.slug,
      status: valueOrDefault(data.status, AdminBoxStatus.Draft),
      symbolFile: data.symbolFile,
      cosmeticBrandId: data.cosmeticBrandId,
      boxCategoryId: data.boxCategoryId,
      cosmetics: data.cosmetics,
      warehouseProductId: data.warehouseProductId,
      maxQuantityPerOrder: data.maxQuantityPerOrder,
      contentPreviewUrl: data.contentPreviewUrl,
      currentEdition: data.currentEdition,
      publishedAt: data.publishedAt,
    });
  }

  private buildForm(): AdminProductDetailsFormGroup {
    return this.builder.group({
      attributes: this.builder.control<Nillable<AdminBoxAttributes>>(null),
      characteristics:
        this.builder.control<Nillable<AdminBoxCharacteristics>>(null),
      boxCategoryId: this.builder.control<Nillable<Guid>>(null),
      cosmeticBrandId: this.builder.control<Nillable<Guid>>(null, [
        Validators.required,
      ]),
      cosmetics: this.builder.control<Nillable<AdminBoxCosmetic[]>>(null),
      featuredFile: this.builder.control<Nillable<AdminMultimediaImageUpload>>(
        null,
        [Validators.required],
      ),
      files: this.builder.control<Nillable<AdminMultimediaImageUpload[]>>(null),
      illustrationFile: this.builder.control<
        Nillable<AdminMultimediaImageUpload>
      >(null, [Validators.required]),
      name: this.builder.nonNullable.control<string>('', [
        Validators.required,
        Validators.maxLength(AdminBoxDetailsForm.NameMaxLength),
      ]),
      password: this.builder.control<Nillable<string>>(null, [
        Validators.maxLength(AdminBoxDetailsForm.PasswordMaxLength),
      ]),
      price: this.builder.control<Nillable<number>>(null, [
        Validators.min(0),
        Validators.required,
      ]),
      availableQuantity: this.builder.control<Nillable<number>>(null, [
        Validators.min(0),
      ]),
      slug: this.builder.control<Nillable<string>>(null),
      status: this.builder.control<Nillable<AdminBoxStatus>>(
        AdminBoxStatus.Draft,
        [Validators.required],
      ),
      symbolFile: this.builder.control<Nillable<AdminMultimediaImageUpload>>(
        null,
        [Validators.required],
      ),
      warehouseProductId: this.builder.control<Nillable<Guid>>(null),
      maxQuantityPerOrder: this.builder.control<Nillable<number>>(null),
      contentPreviewUrl: this.builder.control<Nillable<string>>(null),
      currentEdition: this.builder.control<Nillable<boolean>>(false),
      publishedAt: this.builder.control<Nillable<Date>>(null),
    });
  }

  private fillByData(): void {
    const data = this.data();

    this.form.patchValue({
      attributes: data?.attributes,
      characteristics: data?.characteristics,
      featuredFile: data?.featuredFile,
      files: data?.files,
      illustrationFile: data?.illustrationFile,
      name: data?.name,
      password: data?.password,
      price: data?.price,
      availableQuantity: data?.availableQuantity,
      slug: data?.slug,
      status: valueOrDefault(data?.status, AdminBoxStatus.Draft),
      symbolFile: data?.symbolFile,
      cosmeticBrandId: data?.cosmeticBrandId,
      cosmetics: data?.cosmetics,
      boxCategoryId: data?.boxCategoryId,
      warehouseProductId: data?.warehouseProductId,
      maxQuantityPerOrder: data?.maxQuantityPerOrder,
      contentPreviewUrl: data?.contentPreviewUrl,
      currentEdition: data?.currentEdition,
      publishedAt: data?.publishedAt,
    });
  }

  private productStatusSelectItems(): Observable<SelectItem<AdminBoxStatus>[]> {
    return this.productStatusProvider.selectItems();
  }

  private boxCategorySelectItems(): Observable<SelectItem<Guid>[]> {
    return this.boxCategoryProvider.fetchSelectItems('id');
  }

  private brandSelectItems(): Observable<SelectItem<Guid>[]> {
    return this.brandDataSource.fetchCollectionAsSelectItems('id');
  }

  private shouldPasswordVisibleSource(): Observable<boolean> {
    return this.form.controls.status.valueChanges.pipe(
      startWith(this.form.controls.status.value),
      map((status) => this.isProtected(status)),
      tap((isProtected) => this.configurePasswordRequirements(isProtected)),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  private configureShouldPasswordVisibleSource(): void {
    this.shouldPasswordVisible$ = this.shouldPasswordVisibleSource();
    this.changeDetectorRef.detectChanges();
  }

  private configurePasswordRequirements(required: boolean): void {
    this.changeRequirementsForControl({
      control: this.form.controls.password,
      required,
    });
  }

  private configureWarehouseGoodSelectItemsSource(): void {
    this.warehouseGoodSelectItems$ = this.warehouseGoodSelectItems();
    this.changeDetectorRef.detectChanges();
  }

  private warehouseGoodSelectItems(
    query: Nillable<string> = null,
  ): Observable<SelectItem<Guid>[]> {
    return adminWarehouseGoodSelectItemsWithCurrentSelection({
      control: this.form.controls.warehouseProductId,
      dataSource: this.warehouseGoodDataSource,
      selectItemsProvider: this.warehouseGoodSelectItemsProvider,
      query,
    });
  }

  private configureCurrentGoodIdSource(): void {
    this.currentGoodId$ =
      this.form.controls.warehouseProductId.valueChanges.pipe(
        startWith(this.form.controls.warehouseProductId.value),
      );
    this.changeDetectorRef.detectChanges();
  }

  private configureWarehouseGoodAvailability(): void {
    const control = this.form.controls.warehouseProductId;
    const canBeWarehouseGoodEdited = this.data()?.canBeWarehouseProductIdEdited;

    if (isNil(canBeWarehouseGoodEdited)) {
      control.enable();
      return;
    }

    canBeWarehouseGoodEdited ? control.enable() : control.disable();
  }

  private initializeWarehouseGoodAvailabilityEffect(): void {
    effect(() => this.configureWarehouseGoodAvailability());
  }

  private configureSlugControlRequirements(required: boolean): void {
    this.changeRequirementsForControl({
      control: this.form.controls.slug,
      required,
    });
  }

  private configureFeaturedFileRequirements(required: boolean): void {
    this.changeRequirementsForControl({
      control: this.form.controls.featuredFile,
      required,
    });
  }

  private configureIllustrationFileRequirements(required: boolean): void {
    this.changeRequirementsForControl({
      control: this.form.controls.illustrationFile,
      required,
    });
  }

  private configureSymbolFileRequirements(required: boolean): void {
    this.changeRequirementsForControl({
      control: this.form.controls.symbolFile,
      required,
    });
  }

  private configurePriceRequirements(required: boolean): void {
    this.changeRequirementsForControl({
      control: this.form.controls.price,
      required,
      min: 0,
    });
  }

  private configureCosmeticBrandIdRequirements(required: boolean): void {
    this.changeRequirementsForControl({
      control: this.form.controls.cosmeticBrandId,
      required,
    });
  }

  private configureCharacteristicsRequirements(required: boolean): void {
    const control = this.form.controls.characteristics;
    control.setValidators(
      required ? [AdminBoxCharacteristicsValidator.min(1)] : [],
    );
    control.updateValueAndValidity({ emitEvent: false });
  }

  private changeRequirementsForControl(params: {
    control: AbstractControl;
    required: boolean;
    min?: number;
  }): void {
    const validators: ValidatorFn[] = [];

    if (params.required) {
      validators.push(Validators.required);
    }

    if (!isNil(params.min)) {
      validators.push(Validators.min(params.min));
    }

    params.control.setValidators(validators);
    params.control.updateValueAndValidity({ emitEvent: false });
  }
}
